import React from "react";
import TextInput from "../../../UI/Inputs/TextInput";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
interface CurrencyInputProps {
  value: number | string;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  field: string;
  label: string;
  disabled?: boolean;
}

/**
 * CurrencyInput Component
 * A reusable currency input that allows decimal values up to 2 decimal places.
 * Uses a text input with numeric validation to ensure proper decimal input handling.
 *
 * @param value - Current input value (number or string)
 * @param error - Validation error message
 * @param onChange - Callback when value changes
 * @param field - Field identifier for form integration
 * @param label - Input label
 * @param disabled - Whether input is disabled
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  error,
  onChange,
  field,
  label,
  disabled = false,
}) => {
  
    /**
   * Handle currency input validation to allow only numbers and up to 2 decimal places.
   * Filters out invalid characters while preserving the decimal point.
   */
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    // Allow empty string
    if (inputValue === "") {
      onChange(e);
      return;
    }

    // Remove leading zeros before decimal point (but keep "0.xx")
    inputValue = inputValue.replace(/^0+(?=\d)/, "");

    // Allow only numbers and a single decimal point
    const validatedValue = inputValue
      .replace(/[^0-9.]/g, "") // Remove non-numeric characters except decimal point
      .replace(/\.(?=.*\.)/g, ""); // Remove all but the first decimal point

    // Ensure at most 2 decimal places
    const parts = validatedValue.split(".");
    if (parts.length > 1) {
      parts[1] = parts[1].substring(0, 2);
      e.target.value = parts.join(".");
    } else {
      e.target.value = validatedValue;
    }

    onChange(e);
  };


  return (
    <>
      <div className="fs-13 fw-400">{label}</div>
      <div className="form-group w-100 input-icon icon-left">
        <div className="fs-16 fw-700 icon-text">$</div>
        <TextInput
        value={value === 0 || value === "0" ? "0" : String(value || "")}
        type="text"
        field={field}
        onChange={handleCurrencyChange}
        placeholder=" "
        disabled={disabled}
        floatingLabel={false}
        inputProps={{
          inputMode: "decimal",
          pattern: "[0-9]*\\.?[0-9]{0,2}",
        }}
      />
      {error && <ErrorMessage message={error} />}
    </div>
  </>
  );
};

export default CurrencyInput;
